<template>
  <form
    id="vf-file-upload--dragndrop"
    class="vf-file-upload--dragndrop"
    @dragover.prevent
    @drop.prevent="handleDrop"
  >
    <span class="vf-file-upload__icon vf-icon-my-documents"></span>
    <span class="vf-upload__text" max-file-uploads="1">{{
      t('documentUpload.dragFileHere')
    }}</span>

    <span
      id="up-to-size-dragnDrop"
      max-file-size-megabyte="20"
      class="vf-upload__size"
      >{{ t('documentUpload.upToSize', { size: displayedMaxSize }) }}</span
    >

    <label
      v-if="!isUploading"
      :for="`vf-file-upload__button_dragndrop-${inputId ?? buttonLabel}`"
      class="vf-btn vf-btn--sm vf-btn__secondary--outline"
      :class="{
        disabled,
      }"
      style="display: inline"
      tabindex="1"
      @click="resetInputOnButtonClick ? resetFileInput() : null"
    >
      {{ buttonLabel }}
    </label>

    <input
      v-if="uploadProgress === undefined && !isUploading"
      :id="`vf-file-upload__button_dragndrop-${inputId ?? buttonLabel}`"
      ref="fileInputRef"
      type="file"
      name="files[]"
      :accept="acceptExtensions"
      :webkitdirectory="uploadBoxMode === UploadBoxMode.DIRECTORY"
      :directory="uploadBoxMode === UploadBoxMode.DIRECTORY"
      :disabled="disabled"
      :multiple="uploadBoxMode === UploadBoxMode.MULTIPLE_FILES"
      @change="handleUpload"
    />

    <div v-else-if="uploadProgress !== undefined">{{ uploadProgress }}%</div>
    <spinner-wrapper
      v-else-if="isUploading"
      id="upload-spinner"
      :size="SpinnerSize.ML"
    ></spinner-wrapper>

    <div id="vf-dragndrop-file-upload__filelist">
      {{ file?.name }}
    </div>
  </form>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import {
    FileExtension,
    UploadBoxMode,
  } from '@/components/UploadBox/UploadBox.types';
  import { SpinnerSize } from '@/components/SpinnerWrapper/SpinnerWrapper.types';

  const {
    resetInputOnButtonClick = false,
    uploadBoxMode = UploadBoxMode.FILE,
    ...props
  } = defineProps<{
    buttonLabel: string;
    acceptExtensions?: FileExtension;
    maxSize: string; // in MB
    uploadProgress?: number;
    inputId?: string;
    isUploading?: boolean;
    resetInputOnButtonClick?: boolean;
    uploadBoxMode?: UploadBoxMode;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'change', payload: File[]): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const file = ref<File | undefined>();

  defineExpose({ resetFileInput, file });

  const displayedMaxSize = computed(() => {
    const maxSize = Number(props.maxSize);

    if (maxSize > 1024) {
      return `${maxSize / 1024} GB`;
    }

    return `${maxSize} MB`;
  });

  async function handleDrop(event: DragEvent): Promise<void> {
    event.preventDefault();

    const entries = Array.from(event.dataTransfer?.items ?? [])
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => !!entry);

    const files: File[] = [];

    for (const entry of entries) {
      if (entry.isFile) {
        const file = await getFileFromEntry(entry as FileSystemFileEntry);
        files.push(file);
      } else if (entry.isDirectory) {
        const dirFiles = await readAllFilesFromDirectory(
          entry as FileSystemDirectoryEntry,
        );
        files.push(...dirFiles);
      }
    }
    if (!validateMaxFilesSize(files)) return;

    /** If only one file is dropped, set it to the file ref for display */
    if (files.length === 1) {
      file.value = files[0];
    }
    emit('change', files);
  }

  function getFileFromEntry(entry: FileSystemFileEntry): Promise<File> {
    return new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
  }

  async function readAllFilesFromDirectory(
    directoryEntry: FileSystemDirectoryEntry,
    path: string = '',
  ): Promise<File[]> {
    const dirReader = directoryEntry.createReader();
    const allEntries: FileSystemEntry[] = [];

    async function readEntries(): Promise<void> {
      return new Promise((resolve, reject) => {
        const readBatch = () => {
          dirReader.readEntries(async (entries) => {
            if (entries.length === 0) {
              resolve();
              return;
            }
            allEntries.push(...entries);
            readBatch();
          }, reject);
        };
        readBatch();
      });
    }
    await readEntries();

    const files: File[] = [];

    for (const entry of allEntries) {
      if (entry.isFile) {
        const file = await getFileFromEntry(entry as FileSystemFileEntry);
        files.push(file);
      } else if (entry.isDirectory) {
        const nestedFiles = await readAllFilesFromDirectory(
          entry as FileSystemDirectoryEntry,
          `${path}${entry.name}/`,
        );
        files.push(...nestedFiles);
      }
    }

    return files;
  }

  function handleUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    if (!files) {
      return;
    }
    if (!validateMaxFilesSize(files)) return;
    /** If only one file is added, set it to the file ref for display */
    if (files.length === 1) {
      file.value = files[0];
    }
    emit('change', files);
  }

  const fileInputRef = ref<HTMLInputElement | null>(null);
  function resetFileInput() {
    if (!fileInputRef.value) return;
    fileInputRef.value.value = '';
    file.value = undefined;
  }

  function validateMaxFilesSize(files: File[]): boolean {
    const totalSize = getTotalSize(files);
    if (totalSize > Number(props.maxSize) * 1024 * 1024) {
      createErrorToast(
        t('documentUpload.errors.fileToBig', {
          maxSize: displayedMaxSize.value,
        }),
      );
      return false;
    }
    return true;
  }

  function getTotalSize(files: File[]): number {
    return files.reduce((total, file) => total + file.size, 0);
  }

  watch(
    () => props.isUploading,
    () => {
      if (props.isUploading) {
        file.value = undefined;
      }
    },
  );
</script>

<style lang="scss" scoped>
  #vf-file-upload--dragndrop {
    display: grid;
    grid-template-rows: 100px 2rem 2rem 5rem 1.5rem 1.5rem;
    justify-items: center;
    align-items: center;

    #upload-spinner {
      width: 30px;
      height: 30px;
    }

    #vf-dragndrop-file-upload__filelist {
      margin-top: 20px;
      text-align: center;
      word-break: break-word;
    }
  }
</style>
